import type { Action } from 'state/actions';
import { isQuoteTypeWithRegulation } from 'state/quotes/utils';
import { addCompositeKey, addOrPatchCompositeKey, patchCompositeKey, removeCompositeKey } from 'state/utils/collection';

import type { QuoteRegulationInfosMap } from './regulation.model';
import { initialRegulationInfos } from './regulation.model';

export function quoteRegulationInfosReducer(
  state: QuoteRegulationInfosMap = {},
  action: Action,
): QuoteRegulationInfosMap {
  switch (action.type) {
    case 'QUOTE_ACKED':
    case 'STREAMING_QUOTE_ACKED':
      if (isQuoteTypeWithRegulation(action.quoteType)) {
        return addCompositeKey(state, action.tabId, action.quoteId, {
          ...initialRegulationInfos,
          kidExpected: action.isKidExpected,
        });
      }
      return state;

    case 'TERMSHEET_DOCUMENT_RECEIVED': {
      return patchCompositeKey(state, action.tabId, action.quoteId, regulation => ({
        termsheetDocument: !action.replacePreviousResources
          ? action.listResources?.concat(regulation.kidDocument ?? [])
          : action.listResources,
        termsheetDocumentError: action.rejections,
        termsheetDocumentTimeout: false,
      }));
    }
    case 'TERMSHEET_DOCUMENT_RELOADED': {
      return addOrPatchCompositeKey(state, action.tabId, action.quoteId, () => ({
        termsheetDocument: action.listResources,
        termsheetDocumentError: action.rejections,
        termsheetDocumentTimeout: false,
      }));
    }
    case 'TERMSHEET_DOCUMENT_TIMEOUT': {
      return patchCompositeKey(state, action.tabId, action.quoteId, () => ({
        termsheetDocumentTimeout: true,
      }));
    }
    case 'KID_DOCUMENT_RECEIVED': {
      return patchCompositeKey(state, action.tabId, action.quoteId, regulation => ({
        kidDocument: !action.replacePreviousResources
          ? action.listResources?.concat(regulation.kidDocument ?? [])
          : action.listResources,
        kidDocumentError: action.rejections,
        kidDocumentTimeout: false,
      }));
    }
    case 'KID_DOCUMENT_RELOADED': {
      return addOrPatchCompositeKey(state, action.tabId, action.quoteId, () => ({
        kidDocument: action.listResources,
        kidDocumentError: action.rejections,
        kidDocumentTimeout: false,
      }));
    }
    case 'KID_DOCUMENT_TIMEOUT': {
      return patchCompositeKey(state, action.tabId, action.quoteId, () => ({
        kidDocumentTimeout: true,
      }));
    }
    case 'PRODUCT_GOVERNANCE_DOCUMENT_RECEIVED':
      return patchCompositeKey(state, action.tabId, action.quoteId, regulation => ({
        productGovernanceDocumentation: !action.replacePreviousResources
          ? action.listResources?.concat(regulation.productGovernanceDocumentation ?? [])
          : action.listResources,
        productGovernanceDocumentationError: action.rejections,
        productGovernanceDocumentationTimeout: false,
      }));
    case 'PRODUCT_GOVERNANCE_DOCUMENT_RELOADED':
      return addOrPatchCompositeKey(state, action.tabId, action.quoteId, () => ({
        productGovernanceDocumentation: action.listResources,
        productGovernanceDocumentationError: action.rejections,
        productGovernanceDocumentationTimeout: false,
      }));
    case 'PRODUCT_GOVERNANCE_DOCUMENT_TIMEOUT':
      return patchCompositeKey(state, action.tabId, action.quoteId, () => ({
        productGovernanceDocumentationTimeout: true,
      }));
    case 'PRODUCT_GOVERNANCE_ASSESSMENT_RECEIVED':
      return patchCompositeKey(state, action.tabId, action.quoteId, () => ({
        productGovernanceAssessment: action.productGovernance,
        productGovernanceAssessmentTimeout: false,
      }));
    case 'PRODUCT_GOVERNANCE_ASSESSMENT_RELOADED':
      return patchCompositeKey(state, action.tabId, action.quoteId, () => ({
        productGovernanceAssessment: action.productGovernance,
        productGovernanceAssessmentTimeout: false,
      }));
    case 'PRODUCT_GOVERNANCE_ASSESSMENT_TIMEOUT':
      return patchCompositeKey(state, action.tabId, action.quoteId, () => ({
        productGovernanceAssessmentTimeout: true,
      }));
    case 'COST_AND_CHARGE_RECEIVED':
      return addOrPatchCompositeKey(state, action.tabId, action.quoteId, () => ({
        costAndCharge: action.costsAndCharges,
        theoreticalValue: action.theoreticalValue,
        costAndChargeReceived: true,
      }));
    case 'COST_AND_CHARGE_RELOADED':
      return addOrPatchCompositeKey(state, action.tabId, action.quoteId, () => ({
        costAndCharge: action.costsAndCharges,
        theoreticalValue: action.theoreticalValue,
        costAndChargeReceived: true,
      }));
    case 'REGULATION_QUOTE_REMOVED':
      return removeCompositeKey(state, action.tabId, action.quoteId);
    default:
      return state;
  }
}
